import { FC, useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import { Chart, Series } from "highcharts";
import { Cube } from "../../../../charts/multi-type-chart/components/common/Cube";
import { chartSeriesHighlightFunctions } from "../../utils/helpers/chartSeriesHighlightFunctions";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";

interface LegendItemProps {
  chartApi: Chart;
  legend: string;
  enabled: boolean;
}

export const LegendItem: FC<LegendItemProps> = ({
  chartApi,
  legend,
  enabled,
}) => {
  const onMouseEnter = useCallback(() => {
    chartSeriesHighlightFunctions.onHoverIn(chartApi, legend);
  }, [chartApi, legend]);

  const onMouseLeave = useCallback(() => {
    chartSeriesHighlightFunctions.onHoverOut(chartApi);
  }, [chartApi]);

  const hideCurrentLegend = useCallback(() => {
    try {
      chartApi.series.forEach((series: Series) => {
        if (series.name === legend) {
          chartSeriesHighlightFunctions.hideLegend(series, enabled);
        }
      });
    } catch (e) {
      console.error(`LegendItem toggleSeries function error message: ${e}`);
    }
  }, [chartApi, legend, enabled]);

  const hideAllLegendBesidesCurrent = useCallback(() => {
    try {
      const allTurnedOffBesidesCurrent = chartApi.series
        .filter(({ name }) => name !== legend)
        .every(({ visible }) => !visible);

      chartApi.series.forEach((series: Series) => {
        if (series.name === legend) {
          chartSeriesHighlightFunctions.hideLegend(series, false);
        }

        if (series.name !== legend) {
          chartSeriesHighlightFunctions.hideLegend(
            series,
            !allTurnedOffBesidesCurrent,
          );
        }
      });
    } catch (e) {
      console.error(`LegendItem toggleSeries function error message: ${e}`);
    }
  }, [chartApi, legend]);

  const currentSeries = useMemo(() => {
    return chartApi?.series?.find((series) => {
      return series.name === legend;
    });
  }, [chartApi, legend]);

  const color = currentSeries?.color ?? "";
  const isLine = currentSeries?.userOptions?.type === "line";
  // @ts-expect-error | typescript problem
  const dashed = currentSeries?.userOptions?.dashStyle === "Dash";

  const xStyles = useMemo(
    () => getXStyles(enabled ? "white" : `${color}`),
    [enabled, color],
  );

  return (
    <Stack
      px={0.5}
      direction="row"
      alignItems="center"
      sx={stackStyles}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Cube
        dashed={dashed}
        width={10}
        height={isLine ? 3 : 10}
        onClick={hideCurrentLegend}
        color={enabled ? `${color}` : "transparent"}
        border={enabled ? undefined : `1px solid ${color}`}
        sx={xStyles}
      />

      <TypographyWithTooltip
        title={legend}
        maxWidth={150}
        variant="caption"
        color="text.secondary"
        onClick={hideAllLegendBesidesCurrent}
        sx={enabled ? undefined : { textDecoration: "line-through" }}
      />
    </Stack>
  );
};

const stackStyles = {
  cursor: "pointer",
  borderRadius: 1,
  "&:hover": {
    bgcolor: "grey.100",
  },
};

export const getXStyles = (color: string) => ({
  position: "relative",
  "&:hover": {
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `${color}`,
      height: "1.5px",
      margin: "auto",
      transform: "rotate(45deg)",
    },
    "&::after": {
      transform: "rotate(-45deg)",
    },
  },
});
